/**
 * Pure landing-route selection, extracted from `ProtectedHomeRedirect`
 * ( routes/index.tsx ) so it can be unit-tested without rendering the router or
 * booting the feature-flag / capability providers. [TV1-2077]
 *
 * Picks the FIRST entry in `orderedEntries` whose feature flag is active AND
 * whose ( optional ) read-capability the user holds — mirroring the route's own
 * `FlaggedRoute` + `GatedRoute` gates so a user is never landed on a page they
 * would immediately be bounced out of. Returns `undefined` when no entry
 * qualifies; the caller then sends the user to /not-found.
 *
 * Order is meaningful: `HOME_REDIRECT_ORDER` ranks the primary nav routes first
 * and the /subscription pay-surface fallback last, so a fully entitled user
 * still lands on their first primary route while a de-entitled / suspended
 * account ( whose flags collapse to the billing surface only — slice 1 ) falls
 * through to /subscription instead of /not-found.
 */
export type HomeRedirectEntry = { path: string; flagKey: string };

export const resolveHomePath = (
  orderedEntries: ReadonlyArray<HomeRedirectEntry>,
  isFlagActive: ( flagKey: string ) => boolean,
  hasCapability: ( path: string ) => boolean
): string | undefined => {
  const target = orderedEntries.find(
    ( entry ) => isFlagActive( entry.flagKey ) && hasCapability( entry.path )
  );
  return target?.path;
};
